import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import { IconCalendarDuotone, IconLiveDuotone } from '@brandlive/pixel';
import MultiButtonConfirmationModal from './multi-button-confirmation-modal';
import { setModalState } from '../../../stores/actions/modal';
import * as BroadcastActions from '../../../stores/actions/broadcasting';
import SlidingButtonGroup from '../../general-components/sliding-button-group';
import Icon, { COLORS, ICONS } from '../../general-components/icon';
import { GreenroomState as GRS } from '../../../stores/types/types';
import { useTotalShotDuration } from '../../../hooks/use-shot-utilities';
import DateTimePicker, { DateTimePickerStyle } from '../../general-components/date-time-picker';
import SelectSmall from '../../general-components/select-small';
import { SelectGroupOption } from '../../general-components/select-group';
import {
	CreateScheduledStream,
	DeleteScheduledStream,
	UpdateScheduledStream,
} from '../../../connection/scheduled-streams';
import {
	ScheduledStreamResponse,
	ScheduledStreamStatus,
	ScheduledStreamValidation,
} from '../../../stores/types/connection';
import StreamingSettings, { StreamSettingLocation } from '../../broadcasting/settings/streaming-settings';
import Socket from '../../../connection/socket';
import StreamingDestinations from '../../broadcasting/settings/streaming-destinations';
import Popover from '../../general-components/popover';
import { BROADCAST_STATES } from '../../../stores/types/broadcasting';
import AnimateHeight from '../../general-components/animate-height';
import useAutoplay from '../../../hooks/use-autoplay';
import { bulkEditShots, setShotTabShotId } from '../../../stores/actions/shots';
import LoadingSpinner from '../../general-components/loading-spinner';
import ShotThumbnail from '../../general-components/shot-thumbnail';
import type { Shot } from '../../../stores/types/shots';
import { SHOT_THUMBNAIL_TYPES } from '../../../stores/types/shots';
import { baseShot } from '../../../stores/reducers/shots';
import useShots from '../../../contexts/shots/provider';
import { setClearShot } from '../../../stores/actions/settings';
import { withinUnsafeTimeLeft } from '../../../utils/scheduled-streaming';
import { showAlert } from '../../general-components/alert-service';
import StreamingResolution from '../../broadcasting/settings/streaming-resolution';
import DestinationCount from '../../broadcasting/settings/destination-count';
import useScrollListOverflow from '../../../hooks/use-scroll-list-overflow';
import { setActiveTab, setRightDrawerOpen } from '../../../stores/actions/tabs';
import { TABS } from '../../../stores/types/tabs';

export interface AllShotsAutoplayError extends ScheduledStreamValidation {
	shot: Shot;
	label: string;
}

export interface LiveStreamConfirmationModalProps {
	socket: Socket | null;
	initialMode?: LiveStreamStart;
}

export enum LiveStreamStart {
	StartNow,
	Schedule,
}

enum ScheduleOption {
	Prior5 = 'prior-5',
	Prior10 = 'prior-10',
	Prior15 = 'prior-15',
	OnSchedule = 'on-schedule',
	Custom = 'custom',
}

enum ScheduleErrorModal {
	Hidden = 'hidden',
	ErrorList = 'error-list',
	Confirm = 'confirm',
}

const scheduleOptions: SelectGroupOption[] = [
	{ value: ScheduleOption.Prior5, label: '5 minutes prior' },
	{ value: ScheduleOption.Prior10, label: '10 minutes prior' },
	{ value: ScheduleOption.Prior15, label: '15 minutes prior' },
	{ value: ScheduleOption.OnSchedule, label: 'On schedule' },
	{ value: ScheduleOption.Custom, label: 'Custom' },
];
const SCHEDULE_DEFAULT = scheduleOptions[3];
const {
	STARTING_UP,
	BROADCAST_PREWARMING,
} = BROADCAST_STATES;

const minToMs = (minutes: number) => (minutes * 60 * 1000);

const getScheduleOption = (
	initialMode: LiveStreamStart,
	now: number,
	meetingStartTime: Date = new Date(),
	scheduledStartTime: Date | null = null,
): ScheduleOption => {
	const meetingUtcTime = new Date(meetingStartTime).valueOf();
	const scheduledUtcTime = scheduledStartTime?.valueOf() ?? 0;

	if (
		initialMode === LiveStreamStart.StartNow
		|| !scheduledStartTime
		|| scheduledUtcTime <= now
		|| scheduledUtcTime === meetingUtcTime
	) {
		return ScheduleOption.OnSchedule;
	}

	const prior5 = meetingUtcTime - minToMs(5);
	const prior10 = meetingUtcTime - minToMs(10);
	const prior15 = meetingUtcTime - minToMs(15);

	if (scheduledStartTime.valueOf() === prior5) {
		return ScheduleOption.Prior5;
	}

	if (scheduledStartTime.valueOf() === prior10) {
		return ScheduleOption.Prior10;
	}

	if (scheduledStartTime.valueOf() === prior15) {
		return ScheduleOption.Prior15;
	}

	return ScheduleOption.Custom;
};

const getCustomDate = (
	initialMode: LiveStreamStart,
	now: number,
	meetingStartTime: Date = new Date(),
	scheduledStartTime: Date | null = null,
) => {
	if (
		initialMode === LiveStreamStart.Schedule
		&& scheduledStartTime
		&& scheduledStartTime.valueOf() > now
	) {
		return scheduledStartTime;
	}

	return new Date(meetingStartTime);
};

export default function LiveStreamConfirmation(props: LiveStreamConfirmationModalProps) {
	const {
		socket,
		initialMode = LiveStreamStart.StartNow,
	} = props;

	const dispatch = useDispatch();

	const shots = useSelector((state: GRS) => state.ShotsReducer.shots);
	const reduxMeeting = useSelector((state: GRS) => state.SessionReducer.meeting);
	const reduxHomeMeeting = useSelector((state: GRS) => state.HomeReducer.homeMeeting);
	const user = useSelector((state: GRS) => state.SessionReducer.user);
	const addingDestination = useSelector((state: GRS) => state.BroadcastReducer.addingDestination);
	const editingDestination = useSelector((state: GRS) => state.BroadcastReducer.editingDestination);
	const scheduledStream = useSelector((state: GRS) => state.MeetingReducer.scheduledStream);
	const currentShotId = useSelector((state: GRS) => state.ShotsReducer.currentShotId);
	const serverState = useSelector((state: GRS) => state.BroadcastReducer.serverState);
	const meeting = reduxMeeting || reduxHomeMeeting;
	const token = reduxMeeting
		? useSelector((state: GRS) => state.SessionReducer.token)
		: process.env.REACT_APP_SYNC_AUTH;
	const shotStartTimestampEntity = useSelector(
		(state: GRS) => state.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const {
		use_spielberg: useSpielberg,
	} = useSelector((state: GRS) => state.SettingsReducer.featureFlags);

	const totalDuration = useTotalShotDuration();
	const { getAllShotsChangeAutoplay } = useAutoplay();
	const { handleSelectShot } = useShots();
	const {
		showTop,
		showBottom,
		showBoth,
		scrollList,
	} = useScrollListOverflow();

	const [liveStreamStart, setLiveStreamStart] = useState<LiveStreamStart>(initialMode);
	const [now, setNow] = useState<number>(Date.now());

	// Unix timestamp
	const [scheduledStart, setScheduledStart] = useState<number>(
		scheduledStream?.startTime?.valueOf() ?? 0,
	);
	const [scheduleOption, setScheduleOption] = useState<ScheduleOption>(getScheduleOption(
		initialMode,
		now,
		meeting?.start_time,
		scheduledStream?.startTime,
	));
	const [customStartDate, setCustomStartDate] = useState<Date>(getCustomDate(
		initialMode,
		now,
		meeting?.start_time,
		scheduledStream?.startTime,
	));
	const [destinationPopoverOpen, setDestinationPopoverOpen] = useState<boolean>(false);
	const [schedulingErrors, setSchedulingErrors] = useState<AllShotsAutoplayError[]>([]);
	const [scheduleErrorModal, setScheduleErrorModal] = useState<ScheduleErrorModal>(
		ScheduleErrorModal.Hidden,
	);

	const interval = useRef<NodeJS.Timeout>();
	const scheduleNotification = useRef<HTMLDivElement>(null);

	const formatTime = (date: number | Date, includeMonthAndDay: boolean = false) => (
		new Date(date).toLocaleTimeString('en-US', {
			...(includeMonthAndDay && { month: 'short', day: 'numeric' }),
			hour: 'numeric',
			minute: 'numeric',
			timeZoneName: 'short',
		})
	);

	const startDateNumber = useMemo((): number => (
		liveStreamStart === LiveStreamStart.StartNow ? now : scheduledStart
	), [liveStreamStart, now, scheduledStart]);

	const endDateNumber = useMemo((): number => (
		liveStreamStart === LiveStreamStart.StartNow
			? now + (totalDuration * 1000)
			: scheduledStart + (totalDuration * 1000)
	), [liveStreamStart, now, scheduledStart, totalDuration]);

	function addDestination(newDestination: SelectGroupOption) {
		dispatch(BroadcastActions.setAddingDestination(
			{
				name: newDestination.label,
				url: '',
				streamKey: '',
				logo: '',
				type: newDestination.value,
				selected: false,
			},
		));
		setDestinationPopoverOpen(false);
	}

	function closeModal() {
		dispatch(setModalState({ modalOpen: false }));
	}

	useEffect(() => {
		if (interval.current) {
			clearInterval(interval.current);
		}

		interval.current = setInterval(() => {
			setNow(Date.now());
			if (scheduledStream?.startTime) {
				if (withinUnsafeTimeLeft(scheduledStream.startTime)) {
					closeModal();
				}
			}
		}, 1000);

		return () => clearInterval(interval.current);
	}, [scheduledStream?.startTime]);

	useEffect(() => {
		if (meeting) {
			let startTime = new Date(meeting.start_time).valueOf();

			switch (scheduleOption) {
				case ScheduleOption.Prior5:
					startTime = new Date(meeting.start_time).valueOf() - minToMs(5);
					break;
				case ScheduleOption.Prior10:
					startTime = new Date(meeting.start_time).valueOf() - minToMs(10);
					break;
				case ScheduleOption.Prior15:
					startTime = new Date(meeting.start_time).valueOf() - minToMs(15);
					break;
				case ScheduleOption.Custom:
					startTime = customStartDate.valueOf();
					break;
				case ScheduleOption.OnSchedule:
				default:
					break;
			}

			setScheduledStart(startTime);
		}
	}, [meeting, scheduleOption, customStartDate]);

	const durationString = useMemo(() => {
		const day = Math.floor(totalDuration / (3600 * 24));
		const hour = Math.floor((totalDuration % (3600 * 24)) / 3600);
		const min = Math.floor((totalDuration % 3600) / 60);
		return `${day > 0 ? `${day}d ` : ''}${hour > 0 ? `${hour}h ` : ''}${min > 0 ? `${min}m` : ''}`;
	}, [totalDuration]);

	const currentScheduleOption = useMemo(() => (
		scheduleOptions.find(
			({ value }) => value === scheduleOption,
		) || SCHEDULE_DEFAULT
	), [scheduleOption]);

	const setScheduleOptionValue = (option: ScheduleOption) => {
		setScheduleOption(scheduleOptions.find(
			({ value }) => value === option,
		)?.value || ScheduleOption.OnSchedule);
	};

	const hasFutureScheduledStream = useMemo((): boolean => (
		!!(scheduledStream && scheduledStream.startTime.valueOf() > now)
	), [now, scheduledStream]);

	useEffect(() => {
		if (hasFutureScheduledStream) {
			setLiveStreamStart(LiveStreamStart.Schedule);
		}
	}, [hasFutureScheduledStream]);

	const userHasEditedScheduledStream = useMemo((): boolean => {
		if (!scheduledStream) {
			return false;
		}

		if (scheduledStream.startTime.valueOf() !== startDateNumber) {
			return true;
		}

		return false;
	}, [scheduledStream, startDateNumber]);

	const isInitializing = useMemo(() => (
		useSpielberg ? false : (serverState === STARTING_UP || serverState === BROADCAST_PREWARMING)
	), [serverState, useSpielberg]);

	const firstButtonText = useMemo((): string => (
		hasFutureScheduledStream && !userHasEditedScheduledStream
			? 'Close'
			: 'Cancel'
	), [hasFutureScheduledStream, userHasEditedScheduledStream]);

	const secondButtonText = useMemo((): string => {
		if (liveStreamStart === LiveStreamStart.StartNow) {
			if (isInitializing) {
				return 'Initializing';
			}

			return 'Stream';
		}

		if (hasFutureScheduledStream && userHasEditedScheduledStream) {
			return 'Save changes';
		}

		if (hasFutureScheduledStream) {
			return 'Cancel schedule';
		}

		return 'Schedule';
	}, [
		hasFutureScheduledStream,
		isInitializing,
		liveStreamStart,
		userHasEditedScheduledStream,
	]);

	const secondButtonIcon = useMemo((): JSX.Element => {
		if (liveStreamStart === LiveStreamStart.StartNow && isInitializing) {
			return (
				<LoadingSpinner
					height={10}
					width={10}
					color="#09090A"
					stroke={10}
					style={{ display: 'flex', margin: 'none' }}
				/>
			);
		}

		if (liveStreamStart === LiveStreamStart.StartNow && !isInitializing) {
			return (
				<IconLiveDuotone
					size={12}
					color={'var(--primitives-mode-1-gray-1200)'}
				/>
			);
		}

		return <></>;
	}, [liveStreamStart, isInitializing]);

	async function createScheduledStream(): Promise<ScheduledStreamResponse | null> {
		if (token && meeting?.meeting && user?.gr_user) {
			return CreateScheduledStream(
				token,
				meeting.meeting,
				user.gr_user,
				new Date(startDateNumber),
			);
		}

		return null;
	}

	async function updateScheduledStream(): Promise<ScheduledStreamResponse | null> {
		if (token && meeting?.meeting && scheduledStream?.id && user?.gr_user) {
			return UpdateScheduledStream(
				token,
				meeting.meeting,
				scheduledStream.id,
				user.gr_user,
				new Date(startDateNumber),
				ScheduledStreamStatus.Scheduled,
			);
		}

		return null;
	}

	async function deleteScheduledStream(): Promise<boolean> {
		if (token && meeting?.meeting && scheduledStream?.id && user?.gr_user) {
			return DeleteScheduledStream(
				token,
				meeting.meeting,
				scheduledStream.id,
				user.gr_user,
			);
		}

		return false;
	}

	function buildValidationErrors(
		allShots: Shot[],
		validationResults: ScheduledStreamValidation[],
	): AllShotsAutoplayError[] {
		// Create a map of all shots for easy lookup
		const allShotsMap = allShots.reduce((acc, shot) => {
			acc[shot.shot_id] = shot;

			return acc;
		}, {} as { [key: number]: Shot });

		return validationResults.reduce((acc, validationResult, index) => {
			// Avoid having to check for null with baseShot
			const shot = allShotsMap[validationResult.shotId] ?? baseShot;

			if (!validationResult.isValid) {
				return [
					...acc,
					{
						...validationResult,
						shot,
						label: shot?.shot_data.shotTitle || `Shot ${index + 1}`,
					},
				];
			}

			return acc;
		}, [] as AllShotsAutoplayError[]);
	}

	function cancel() {
		if (scheduledStream && userHasEditedScheduledStream) {
			setScheduledStart(scheduledStream.startTime.valueOf());
			setScheduleOption(getScheduleOption(
				LiveStreamStart.Schedule,
				now,
				meeting?.start_time,
				scheduledStream?.startTime,
			));
		}

		closeModal();
	}

	async function goLive(ignoreErrors: boolean = false) {
		if (liveStreamStart === LiveStreamStart.StartNow) {
			if (isInitializing) {
				return;
			}

			dispatch(BroadcastActions.setGoLiveFromModal(true));
		} else if (liveStreamStart === LiveStreamStart.Schedule) {
			const timeIsValid = startDateNumber >= (now + (3 * 60 * 1000))
				|| ignoreErrors;

			if (!timeIsValid) {
				showAlert({
					message: 'Cannot schedule stream',
					description: 'Must be 3+ minutes in the future',
					type: 'media-device',
				});

				// Leave the modal open and do not advance to the error screen
				return;
			}

			const autoplayResult = await getAllShotsChangeAutoplay(
				shots,
				true,
				currentShotId,
				shotStartTimestampEntity,
			);
			const errorsFound = autoplayResult.validationResults.some(
				({ isValid }) => !isValid,
			);
			const canContinue = hasFutureScheduledStream
				|| ignoreErrors
				|| !errorsFound;

			if (token && autoplayResult.affectedShots.length > 0) {
				dispatch(bulkEditShots(token, autoplayResult.affectedShots));
			}

			if (errorsFound && !canContinue) {
				setSchedulingErrors(buildValidationErrors(
					shots,
					autoplayResult.validationResults,
				));
				setScheduleErrorModal(ScheduleErrorModal.ErrorList);
			}

			if (!canContinue) {
				return;
			}

			if (!scheduledStream) {
				createScheduledStream();
			} else if (userHasEditedScheduledStream) {
				const updateScheduledStreamResult = await updateScheduledStream();

				if (updateScheduledStreamResult) {
					setScheduleOption(getScheduleOption(
						LiveStreamStart.Schedule,
						now,
						meeting?.start_time,
						new Date(updateScheduledStreamResult.startTime),
					));
				}
			} else if (!userHasEditedScheduledStream) {
				deleteScheduledStream();
			}
		}

		closeModal();
	}

	function goToShot(shotId: number) {
		const foundShot = shots.find(
			({ shot_id: curShotId }) => curShotId === shotId,
		);

		if (foundShot) {
			if (currentShotId !== shotId) {
				handleSelectShot(shotId);
			}

			dispatch(setClearShot(false));
			dispatch(setShotTabShotId(shotId));
			dispatch(setActiveTab(TABS.SHOTS));
			dispatch(setRightDrawerOpen(true));
		}

		cancel();
	}

	return (
		<>
			{scheduleErrorModal === ScheduleErrorModal.Hidden && (
				<MultiButtonConfirmationModal
					key="scheduled-stream-confirm"
					headerText="Livestream"
					firstButtonText={firstButtonText}
					firstButtonAction={cancel}
					secondButtonText={secondButtonText}
					secondButtonAction={() => {
						goLive(hasFutureScheduledStream && !userHasEditedScheduledStream);
					}}
					className="confirmation-modal"
					isOpen
					buttonAlignment="right"
					buttonAutoWidth={true}
					secondButtonWarn={hasFutureScheduledStream && !userHasEditedScheduledStream}
					secondButtonIcon={secondButtonIcon}
					secondButtonClassName={classNames({
						initializing: isInitializing && liveStreamStart === LiveStreamStart.StartNow,
					})}
					buttonRowContent={(
						<CSSTransition
							in={
								liveStreamStart === LiveStreamStart.Schedule
								&& !hasFutureScheduledStream
							}
							nodeRef={scheduleNotification}
							timeout={250}
							classNames="fade"
							unmountOnExit
						>
							<div
								className="autoplay-notification"
								ref={scheduleNotification}
							>
								<Icon
									name={ICONS.ERROR}
									color={COLORS.SEMANTIC_RED_500}
									size={14}
								/>
								<div className="padding-left-small bl-font-p2">
									All shots will be set to autoplay
								</div>
							</div>
						</CSSTransition>
					)}
					{...(hasFutureScheduledStream && {
						customHeader: (
							<div className="modal-custom-header-container">
								<Icon
									name={ICONS.TIMER}
									size={20}
									color={COLORS.TEXT_GRAY_1200}
								/>
								<span
									className="text-white font-weight-bold font-size-16 modal-custom-header-text"
								>
									Scheduled Livestream
								</span>
							</div>
						),
					})}
				>
					{(liveStreamStart === LiveStreamStart.StartNow || !hasFutureScheduledStream) && (
						<SlidingButtonGroup
							activeButton={liveStreamStart}
							slidingButtons={[
								{
									title: 'Start Now',
									subtitle: 'Go live instantly',
									icon: <IconLiveDuotone size={24} color={liveStreamStart === LiveStreamStart.StartNow ? 'var(--primitives-mode-1-gray-1200)' : 'var(--primitives-mode-1-gray-550)'} />,
									onClick: () => { setLiveStreamStart(LiveStreamStart.StartNow); },
								},
								{
									title: 'Schedule',
									subtitle: 'Go live in the future',
									icon: <IconCalendarDuotone size={24} color={liveStreamStart === LiveStreamStart.Schedule ? 'var(--primitives-mode-1-gray-1200)' : 'var(--primitives-mode-1-gray-550)'} />,
									onClick: () => { setLiveStreamStart(LiveStreamStart.Schedule); },
								},
							]}
						/>
					)}
					<AnimateHeight visible={liveStreamStart === LiveStreamStart.Schedule}>
						<div className="schedule-module">
							<div className="schedule-module__selector">
								<h5 className="schedule-module__selector-label bl-font-header">When</h5>
								<SelectSmall
									options={scheduleOptions}
									selected={currentScheduleOption}
									onChange={setScheduleOptionValue}
									dropdown={true}
									className="schedule-module__selector-dropdown"
									label={''}
								/>
							</div>
							<AnimateHeight visible={scheduleOption === ScheduleOption.Custom}>
								<div className="schedule-module__custom-time">
									<div className="schedule-module__custom-time-label bl-font-s2">
										Custom
									</div>
									<DateTimePicker
										dateOrTime={DateTimePickerStyle.DATE}
										startDate={customStartDate}
										setStartDate={(date: Date) => setCustomStartDate(date)}
									/>
									<DateTimePicker
										dateOrTime={DateTimePickerStyle.TIME}
										startDate={customStartDate}
										setStartDate={(date: Date) => setCustomStartDate(date)}
									/>
								</div>
							</AnimateHeight>
						</div>
					</AnimateHeight>
					<div className="time-module">
						<div>
							<h6 className="bl-font-header text-gray-400">
								{liveStreamStart === LiveStreamStart.StartNow ? 'Time' : 'Scheduled time'}
							</h6>
							<h5
								className={classNames(
									'bl-font-header',
									liveStreamStart === LiveStreamStart.StartNow
										? 'start-now'
										: 'schedule',
								)}
							>
								{
									liveStreamStart === LiveStreamStart.StartNow
										? formatTime(startDateNumber, false)
										: formatTime(startDateNumber, true)
								}
							</h5>
						</div>
						<div className="divider" />
						<div>
							<h6 className="bl-font-header text-gray-400">Duration</h6>
							<h5 className="bl-font-header text-gray-200">
								{durationString || 'None'}
							</h5>
						</div>
						<div>
							<h6 className="bl-font-header text-gray-400">End time</h6>
							<h5 className="bl-font-header text-gray-200">
								{formatTime(endDateNumber)}
							</h5>
						</div>
						<div>
							<h6 className="bl-font-header text-gray-400">Shots</h6>
							<h5 className="bl-font-header text-gray-200">{shots.length}</h5>
						</div>
					</div>
					<StreamingResolution />
					<div className="live-stream-destinations">
						<div className="live-stream-destinations-title">
							<h5 className="bl-font-header">Where</h5>
							<DestinationCount location={StreamSettingLocation.StreamModal} />
						</div>
						<div className="live-stream-destinations-body">
							<div
								className="scroll-shadow"
								style={{
									top: '0px',
								}}
							/>
							{meeting && (
								<StreamingSettings
									socket={socket}
									meeting={meeting}
									location={StreamSettingLocation.StreamModal}
								/>
							)}
							<div
								className="scroll-shadow"
								style={{
									bottom: '64px',
									transform: 'rotate(180deg)',
								}}
							/>
							<div className="position-relative flex-center">
								<button
									className={classNames('add-destination-button', { showing: !addingDestination && !editingDestination })}
									onClick={() => setDestinationPopoverOpen(true)}
									disabled={!!addingDestination || !!editingDestination}
								>
									<Icon name={ICONS.ADD} color={COLORS.TEXT_GRAY_400} size={16} />
									<h5 className="bl-font-l1 grayscale-400 padding-left-small">Add Destination</h5>
								</button>
							</div>
						</div>
					</div>
					<div className="destination-options-container">
						<Popover
							open={destinationPopoverOpen}
							close={() => setDestinationPopoverOpen(false)}
							className='destination-options'
							position='right'
						>
							<StreamingDestinations buttonCallback={addDestination} buttonClassNames='option background-light' />
						</Popover>
					</div>
				</MultiButtonConfirmationModal>
			)}

			{scheduleErrorModal === ScheduleErrorModal.ErrorList && (
				<MultiButtonConfirmationModal
					key="scheduled-stream-error"
					// This will never show with a custom header
					headerText="Autoplay Errors"
					firstButtonText="Ignore Errors and Schedule Anyway"
					firstButtonAction={() => setScheduleErrorModal(ScheduleErrorModal.Confirm)}
					secondButtonText="Back to Show"
					secondButtonAction={cancel}
					className="confirmation-modal confirm-error error-list"
					isOpen
					buttonAlignment="right"
					firstButtonNoBackground={true}
					buttonAutoWidth={false}
					customHeader={(
						<div className="modal-custom-header-container-error">
							<h6 className="bl-font-header semantic-red-400">
								Error Scheduling Stream
							</h6>
							<div className="bl-font-s3 grayscale-300">
								Please review the following shots before scheduling
							</div>
						</div>
					)}
				>
					<div
						className={classNames('error-scroll', {
							'error-scroll--top': showTop || showBoth,
							'error-scroll--bottom': showBottom || showBoth,
						})}
						ref={scrollList}
					>
						<div className="error-container">
							{schedulingErrors.map(({ label, message, shot }) => (
								<div className="error-item" key={shot.shot_id}>
									<div className="scheduled-stream-error__thumbnail">
										<ShotThumbnail
											borderRatio={0.7}
											shot={shot}
											shotThumbnailType={
												SHOT_THUMBNAIL_TYPES.TELEPROMPTER
											}
										/>
									</div>
									<div className="scheduled-stream-error__description bl-font-p2 grayscale-200">
										<span>{label}:&nbsp;</span>
										<span className="semantic-red-400">
											{message}
										</span>
									</div>
									<button
										className="scheduled-stream-error__action"
										onClick={() => goToShot(shot.shot_id)}
									>
										Go to shot
									</button>
								</div>
							))}
						</div>
					</div>
				</MultiButtonConfirmationModal>
			)}

			{scheduleErrorModal === ScheduleErrorModal.Confirm && (
				<MultiButtonConfirmationModal
					key="scheduled-stream-confirm"
					headerText="Are you sure?"
					firstButtonText="Schedule Anyway"
					firstButtonAction={() => goLive(true)}
					secondButtonText="Cancel"
					secondButtonAction={cancel}
					className="confirmation-modal confirm-error confirm-stream"
					isOpen
					buttonAlignment="right"
				>
					<div className="bl-font-p2 grayscale-200">
						If a producer is not present in the greenroom when your show goes live,
						shots without content or durations will be skipped.
					</div>
				</MultiButtonConfirmationModal>
			)}
		</>
	);
}
